import {
    CdkDrag,
    CdkDragPlaceholder,
    CdkDropList,
    type CdkDragDrop,
} from '@angular/cdk/drag-drop';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import {
    DialogService,
    QpxButton,
    QpxDropSurface,
    QpxIcon,
    QpxTypography,
    toDialogConfirmation,
} from '@quipex/shared/ui';
import { take } from 'rxjs';
import { FocusRestoration } from '../focus-restoration';
import { ProposalState } from '../proposal-state';
import { QuippyPackExcludedDocuments } from './excluded-documents/excluded-documents';
import { QuippyPackRequirementEditor } from './requirement-editor/requirement-editor';
import { QuippyPackSectionHeading } from './section-heading/section-heading';

@Component({
    selector: 'qpx-quippy-pack-editor',
    imports: [
        CdkDrag,
        CdkDragPlaceholder,
        CdkDropList,
        MatButtonModule,
        NgIcon,
        QpxButton,
        QpxDropSurface,
        QpxIcon,
        QpxTypography,
        QuippyPackRequirementEditor,
        QuippyPackSectionHeading,
        QuippyPackExcludedDocuments,
    ],
    templateUrl: './pack-editor.html',
    styleUrl: './pack-editor.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'grid min-w-0 gap-4' },
})
export class QuippyPackEditor {
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialog = inject(DialogService);
    private readonly focus = inject(FocusRestoration);
    protected readonly draft = inject(ProposalState);
    public readonly smallHeaders = input(false);
    public readonly actionId = input.required<string>();
    public readonly disabled = input(false);
    public readonly changed = output<void>();
    protected readonly draggingRequirement = signal(false);
    protected readonly sectionPlaceholderHeight = signal(0);
    protected readonly requirementPlaceholderHeight = signal(0);
    protected readonly listIds = computed(() =>
        this.draft
            .groups()
            .map((group) => `${this.actionId()}-requirements-${group.key}`)
    );
    protected captureSectionHeight(event: PointerEvent): void {
        if (!(event.currentTarget instanceof HTMLElement)) return;
        this.sectionPlaceholderHeight.set(
            event.currentTarget.getBoundingClientRect().height
        );
    }
    protected captureRequirementHeight(event: PointerEvent): void {
        if (!(event.currentTarget instanceof HTMLElement)) return;
        this.requirementPlaceholderHeight.set(
            event.currentTarget.getBoundingClientRect().height
        );
    }
    protected addSection(): void {
        if (this.disabled()) return;
        const key = this.draft.addSection();
        this.changed.emit();
        this.focus.afterRender(`${this.actionId()}-section-${key}`);
    }
    protected addRequirement(section: string): void {
        if (this.disabled()) return;
        const id = this.draft.addRequirement(section);
        this.changed.emit();
        this.focus.afterRender(`${this.actionId()}-requirement-${id}`);
    }
    protected dropSection(event: CdkDragDrop<unknown>): void {
        if (
            this.disabled() ||
            !event.isPointerOverContainer ||
            event.previousContainer !== event.container
        )
            return;
        this.draft.moveSection(event.previousIndex, event.currentIndex);
        this.changed.emit();
    }
    protected dropRow(event: CdkDragDrop<string, string, string>): void {
        if (this.disabled() || !event.isPointerOverContainer) return;
        this.draft.moveRow(
            event.item.data,
            event.container.data,
            event.currentIndex
        );
        this.changed.emit();
    }
    protected removeSection(name: string): void {
        if (this.disabled()) return;
        const section = this.draft.groups().find((group) => group.key === name);
        if (!section) return;
        const actionId = this.actionId();
        const documentCount = section.rows.reduce(
            (count, row) => count + row.documents.length,
            0
        );
        this.dialog
            .openConfirmationDialog(
                {
                    title: 'Delete section',
                    text1: `Delete “${section.name.trim() || 'section'}” and its ${section.rows.length} ${section.rows.length === 1 ? 'requirement' : 'requirements'} from this draft? ${documentCount} ${documentCount === 1 ? 'document remains' : 'documents remain'} available to assign elsewhere.`,
                    confirmButton: 'Delete section',
                    cancelButton: 'Cancel',
                    status: 'destructive',
                },
                { restoreFocus: true }
            )
            .afterClosed()
            .pipe(
                toDialogConfirmation(),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((confirmed) => {
                if (
                    !confirmed ||
                    this.disabled() ||
                    this.actionId() !== actionId ||
                    !this.draft.groups().includes(section)
                )
                    return;
                this.draft.removeSection(name);
                this.changed.emit();
                this.focus.afterRender();
            });
    }
}
